import React, { useState, useEffect, useCallback } from 'react'
import { bannerSettingsApi } from '../../services/bannerSettings'
import type { BannerSetting } from '../../services/bannerSettings'
import { Toast } from '../shared/Toast'

// Live preview of banner on a sample vehicle photo
function BannerPreview({ enabled, bannerText, position, backgroundColor, textColor, fontSize }: {
  enabled: boolean
  bannerText: string
  position: string
  backgroundColor: string
  textColor: string
  fontSize: number
}) {
  const text = bannerText || 'Banner Text'
  const bgColor = backgroundColor || '#094673'
  const txtColor = textColor || '#FFFFFF'
  const size = fontSize || 18

  return (
    <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100" style={{ aspectRatio: '4/3' }}>
      {/* Placeholder vehicle image */}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
          <p className="text-xs text-gray-400 mt-2">Vehicle Photo</p>
        </div>
      </div>

      {/* Banner overlay */}
      {enabled && (
        <div
          className={`absolute left-0 right-0 flex items-center justify-center px-4 py-2 ${
            position === 'top' ? 'top-0' : position === 'bottom' ? 'bottom-0' : 'top-1/2 -translate-y-1/2'
          }`}
          style={{
            backgroundColor: position === 'overlay' ? `${bgColor}cc` : bgColor,
            color: txtColor,
            fontSize: `${Math.max(10, Math.min(size, 48))}px`,
          }}
        >
          <span className="font-bold tracking-wide truncate">{text}</span>
        </div>
      )}

      {/* Disabled overlay */}
      {!enabled && (
        <div className="absolute inset-0 bg-gray-900/10 flex items-center justify-center">
          <span className="text-xs text-gray-500 bg-white/80 px-3 py-1 rounded-full font-medium">Banner Disabled</span>
        </div>
      )}
    </div>
  )
}

export function BannerSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<BannerSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Local form state for live preview
  const [formEnabled, setFormEnabled] = useState(false)
  const [formText, setFormText] = useState('')
  const [formPosition, setFormPosition] = useState('top')
  const [formBgColor, setFormBgColor] = useState('#094673')
  const [formTextColor, setFormTextColor] = useState('#FFFFFF')
  const [formFontSize, setFormFontSize] = useState(18)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: BannerSetting) => {
    setFormEnabled(s.enabled)
    setFormText(s.banner_text || '')
    setFormPosition(s.position || 'top')
    setFormBgColor(s.background_color || '#094673')
    setFormTextColor(s.text_color || '#FFFFFF')
    setFormFontSize(s.font_size || 18)
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await bannerSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load banner settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await bannerSettingsApi.update({
        enabled: formEnabled,
        banner_text: formText || null,
        position: formPosition,
        background_color: formBgColor,
        text_color: formTextColor,
        font_size: formFontSize,
      })
      setSetting(updated)
      syncFormState(updated)
      showToast('Banner settings saved', 'success')
    } catch {
      showToast('Failed to save banner settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading banner settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">Photo Banners</h1>
          <p className="text-sm text-grey mt-1">Add a text banner overlay to your vehicle photos.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-secondary">Banner Configuration</h2>
          </div>
          <div className="p-6 space-y-5">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-secondary">Enable Banner</label>
                <p className="text-xs text-grey mt-0.5">Show a text banner on vehicle photos.</p>
              </div>
              <button
                onClick={() => { setFormEnabled(!formEnabled); markChanged() }}
                role="switch"
                aria-checked={formEnabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
                  formEnabled ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                  formEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Banner Text */}
            <div>
              <label htmlFor="banner-text" className="block text-sm font-medium text-secondary mb-1.5">Banner Text</label>
              <input
                id="banner-text"
                type="text"
                value={formText}
                onChange={(e) => { setFormText(e.target.value); markChanged() }}
                placeholder="e.g. CERTIFIED PRE-OWNED"
                disabled={!formEnabled}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>

            {/* Position */}
            <div>
              <label htmlFor="banner-position" className="block text-sm font-medium text-secondary mb-1.5">Position</label>
              <select
                id="banner-position"
                value={formPosition}
                onChange={(e) => { setFormPosition(e.target.value); markChanged() }}
                disabled={!formEnabled}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400 bg-white"
              >
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="overlay">Center Overlay</option>
              </select>
            </div>

            {/* Colors row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="bg-color" className="block text-sm font-medium text-secondary mb-1.5">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    id="bg-color"
                    type="color"
                    value={formBgColor}
                    onChange={(e) => { setFormBgColor(e.target.value); markChanged() }}
                    disabled={!formEnabled}
                    className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed p-0.5"
                  />
                  <input
                    type="text"
                    value={formBgColor}
                    onChange={(e) => { setFormBgColor(e.target.value); markChanged() }}
                    disabled={!formEnabled}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400 font-mono uppercase"
                    maxLength={7}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="text-color" className="block text-sm font-medium text-secondary mb-1.5">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    id="text-color"
                    type="color"
                    value={formTextColor}
                    onChange={(e) => { setFormTextColor(e.target.value); markChanged() }}
                    disabled={!formEnabled}
                    className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed p-0.5"
                  />
                  <input
                    type="text"
                    value={formTextColor}
                    onChange={(e) => { setFormTextColor(e.target.value); markChanged() }}
                    disabled={!formEnabled}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400 font-mono uppercase"
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            {/* Font Size */}
            <div>
              <label htmlFor="font-size" className="block text-sm font-medium text-secondary mb-1.5">
                Font Size <span className="text-grey font-normal">({formFontSize}px)</span>
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="font-size"
                  type="range"
                  min={10}
                  max={48}
                  value={formFontSize}
                  onChange={(e) => { setFormFontSize(parseInt(e.target.value, 10)); markChanged() }}
                  disabled={!formEnabled}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <input
                  type="number"
                  min={10}
                  max={48}
                  value={formFontSize}
                  onChange={(e) => { setFormFontSize(parseInt(e.target.value, 10) || 18); markChanged() }}
                  disabled={!formEnabled}
                  className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-secondary">Live Preview</h2>
            <p className="text-xs text-grey mt-0.5">Preview how the banner will appear on vehicle photos.</p>
          </div>
          <div className="p-6">
            <BannerPreview
              enabled={formEnabled}
              bannerText={formText}
              position={formPosition}
              backgroundColor={formBgColor}
              textColor={formTextColor}
              fontSize={formFontSize}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
